import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Download, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import { api, rupee } from "../lib/api";
import { useStores } from "../lib/stores";
import {
  defaultLast30DaysRange,
  formatDisplayDateTime,
  validateDateRange,
} from "../lib/dateRange";

const PER_PAGE = 50;

const GRID_HEAD = "collections-grid-head";
const GRID_ROW = "collections-grid-row";
const GRID_FOOT = "collections-grid-foot";
const COL_NUM = "collections-col-num";

function methodLabel(method) {
  if (method === "upi") return "UPI";
  if (method === "card") return "Card";
  if (method === "cash") return "Cash";
  return method ? method.charAt(0).toUpperCase() + method.slice(1) : "—";
}

function exportCollectionsExcel(rows, summary, start, end) {
  const sheetRows = rows.map((r) => ({
    "Date & time": r.at,
    "Billing name": r.billing_name || "Walk-in",
    Type: r.type === "return" ? "Return" : "Collection",
    Method: methodLabel(r.method),
    Amount: r.type === "return" ? -r.amount : r.amount,
    Reference: r.reference,
    Invoice: r.invoice_no || "",
  }));
  sheetRows.push({
    "Date & time": "Total",
    "Billing name": "",
    Type: "",
    Method: "",
    Amount: summary.net_total ?? summary.total,
    Reference: `${summary.transactions} collections · ${summary.return_count || 0} returns`,
    Invoice: "",
  });

  const ws = XLSX.utils.json_to_sheet(sheetRows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Collections");
  XLSX.writeFile(wb, `collections_${start}_to_${end}.xlsx`);
}

export default function Collections() {
  const navigate = useNavigate();
  const { activeStoreId } = useStores();
  const defaults = defaultLast30DaysRange();
  const [start, setStart] = useState(defaults.start);
  const [end, setEnd] = useState(defaults.end);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, total_pages: 1, total_rows: 0 });
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async (page = 1) => {
    const err = validateDateRange(start, end);
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const params = { start, end, page, per_page: PER_PAGE };
      if (activeStoreId) params.store_id = activeStoreId;
      const { data } = await api.get("/collections/daily", { params });
      setRows(data.rows || []);
      setSummary(data.summary || null);
      setPagination(data.pagination || { page: 1, total_pages: 1, total_rows: 0 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load collections");
      setRows([]);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [start, end, activeStoreId]);

  useEffect(() => {
    load(1);
  }, [load]);

  const goPage = (page) => {
    if (page < 1 || page > pagination.total_pages) return;
    load(page);
  };

  const exportExcel = async () => {
    const err = validateDateRange(start, end);
    if (err) return toast.error(err);
    setExporting(true);
    try {
      const params = { start, end, all: 1 };
      if (activeStoreId) params.store_id = activeStoreId;
      const { data } = await api.get("/collections/daily", { params });
      const allRows = data.rows || [];
      if (allRows.length === 0) {
        toast.error("No collections to export in this range");
        return;
      }
      exportCollectionsExcel(allRows, data.summary, start, end);
      toast.success("Excel downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const onStartChange = (value) => {
    setStart(value);
    if (value && end) {
      const err = validateDateRange(value, end);
      if (err) toast.error(err);
    }
  };

  const onEndChange = (value) => {
    setEnd(value);
    if (start && value) {
      const err = validateDateRange(start, value);
      if (err) toast.error(err);
    }
  };

  const openSale = (row) => {
    if (row.sale_id) navigate(`/app/sales/${row.sale_id}`);
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="collections-root">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display font-bold text-3xl">Collections</h1>
          <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>
            Every payment & refund · last 30 days default
          </p>
        </div>
        <button
          type="button"
          onClick={exportExcel}
          disabled={exporting || loading}
          className="h-11 px-4 rounded-md border font-semibold inline-flex items-center gap-2 bg-white tactile-btn disabled:opacity-60"
          style={{ borderColor: "var(--border-soft)" }}
          data-testid="collections-export"
        >
          <Download size={16} />
          {exporting ? "Exporting…" : "Export Excel"}
        </button>
      </div>

      <div className="card-soft p-4 mb-4">
        <div className="flex flex-wrap items-end gap-4">
          <div>
            <label className="text-xs font-medium" style={{ color: "var(--text-muted)" }}>From</label>
            <input
              data-testid="collections-start"
              type="date"
              value={start}
              onChange={(e) => onStartChange(e.target.value)}
              className="mt-1 h-11 px-3 rounded-md border bg-white block min-w-[11rem]"
              style={{ borderColor: "var(--border-soft)" }}
            />
          </div>
          <div>
            <label className="text-xs font-medium" style={{ color: "var(--text-muted)" }}>To</label>
            <input
              data-testid="collections-end"
              type="date"
              value={end}
              onChange={(e) => onEndChange(e.target.value)}
              className="mt-1 h-11 px-3 rounded-md border bg-white block min-w-[11rem]"
              style={{ borderColor: "var(--border-soft)" }}
            />
          </div>
          <button
            type="button"
            onClick={() => load(1)}
            disabled={loading}
            className="h-11 px-5 rounded-md text-white font-semibold tactile-btn"
            style={{ background: "var(--primary)" }}
            data-testid="collections-apply"
          >
            Apply
          </button>
        </div>
      </div>

      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
          {[
            { label: "Cash in", value: summary.cash, money: true },
            { label: "UPI in", value: summary.upi, money: true },
            { label: "Card in", value: summary.card, money: true },
            { label: "Collected", value: summary.total, money: true },
            { label: "Returns", value: summary.returns || 0, money: true, negative: true },
            { label: "Net", value: summary.net_total ?? summary.total, money: true, highlight: true },
          ].map((item) => (
            <div key={item.label} className="card-soft p-4">
              <div className="text-xs font-medium uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
                {item.label}
              </div>
              <div
                className="mt-1 font-display font-bold text-xl num"
                style={
                  item.highlight
                    ? { color: "var(--primary)" }
                    : item.negative && item.value > 0
                      ? { color: "var(--error)" }
                      : undefined
                }
              >
                {item.money ? rupee(item.value) : item.value}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="card-soft overflow-hidden">
        <div className="overflow-x-auto">
          <div className="min-w-0">
            <div className={GRID_HEAD} aria-hidden>
              <div>Date & time</div>
              <div>Billing name</div>
              <div>Type</div>
              <div>Method</div>
              <div className={COL_NUM}>Amount</div>
              <div>Reference</div>
            </div>

            {loading && (
              <div className="text-center py-12 text-sm" style={{ color: "var(--text-muted)" }}>
                Loading…
              </div>
            )}

            {!loading && rows.length === 0 && (
              <div className="text-center py-12 text-sm" style={{ color: "var(--text-muted)" }}>
                No collections in this date range.
              </div>
            )}

            {!loading && rows.map((row) => {
              const isReturn = row.type === "return";
              return (
                <div
                  key={row.id}
                  role="button"
                  tabIndex={0}
                  className={`${GRID_ROW} group cursor-pointer`}
                  onClick={() => openSale(row)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      openSale(row);
                    }
                  }}
                  data-testid={`collection-row-${row.id}`}
                >
                  <div className="whitespace-nowrap text-sm">{formatDisplayDateTime(row.at)}</div>
                  <div className="text-sm font-medium truncate min-w-0">{row.billing_name || "Walk-in"}</div>
                  <div>
                    <span
                      className="inline-flex px-2 py-0.5 rounded-full text-[11px] font-semibold uppercase tracking-wide"
                      style={
                        isReturn
                          ? { background: "var(--surface-error)", color: "var(--error)" }
                          : { background: "var(--surface-success)", color: "var(--success)" }
                      }
                    >
                      {isReturn ? "Return" : "Collection"}
                    </span>
                  </div>
                  <div className="text-sm">{methodLabel(row.method)}</div>
                  <div
                    className={`${COL_NUM} num font-semibold`}
                    style={{ color: isReturn ? "var(--error)" : "var(--text-main)" }}
                  >
                    {isReturn ? `−${rupee(row.amount)}` : rupee(row.amount)}
                  </div>
                  <div className="min-w-0">
                    <span
                      className="font-mono text-sm font-semibold truncate block group-hover:underline"
                      style={{ color: "var(--primary)" }}
                    >
                      {row.reference}
                    </span>
                    {row.invoice_no && row.return_no && (
                      <span className="text-xs truncate block mt-0.5" style={{ color: "var(--text-muted)" }}>
                        Invoice {row.invoice_no}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}

            {summary && rows.length > 0 && (
              <div className={GRID_FOOT}>
                <div>Range total</div>
                <div />
                <div className="text-sm" style={{ color: "var(--text-muted)" }}>
                  {summary.transactions} in · {summary.return_count || 0} out
                </div>
                <div />
                <div className={`${COL_NUM} num`} style={{ color: "var(--primary)" }}>
                  {rupee(summary.net_total ?? summary.total)}
                </div>
                <div className="text-sm min-w-0 truncate" style={{ color: "var(--text-muted)" }}>
                  Collected {rupee(summary.total)}
                  {(summary.returns || 0) > 0 && <> · Returns {rupee(summary.returns)}</>}
                </div>
              </div>
            )}
          </div>
        </div>

        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t" style={{ borderColor: "var(--border-soft)" }}>
            <div className="text-sm" style={{ color: "var(--text-muted)" }}>
              Page {pagination.page} of {pagination.total_pages}
              <span className="hidden sm:inline"> · {pagination.total_rows} transactions</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goPage(pagination.page - 1)}
                disabled={pagination.page <= 1 || loading}
                className="h-9 px-3 rounded-md border inline-flex items-center gap-1 text-sm font-medium disabled:opacity-40 bg-white"
                style={{ borderColor: "var(--border-soft)" }}
                data-testid="collections-prev"
              >
                <ChevronLeft size={16} /> Prev
              </button>
              <button
                type="button"
                onClick={() => goPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.total_pages || loading}
                className="h-9 px-3 rounded-md border inline-flex items-center gap-1 text-sm font-medium disabled:opacity-40 bg-white"
                style={{ borderColor: "var(--border-soft)" }}
                data-testid="collections-next"
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
